import React, { useState, useEffect, useRef, useCallback } from "react";
import jsQR from "jsqr";
import nacl from "tweetnacl";
import api from "../api/homeapi";
import { CameraIcon, CheckIcon, XIcon, AlertIcon, WifiIcon, WifiOffIcon } from "../components/Icons";
import { StaffFrame, PageHead } from "./DashboardShared";
import { useNotify } from "../components/Notify";

const C = { green:"#006B3C", greenDk:"#004D2B", greenLt:"#E8F5EE", yellow:"#FFC300", yellowLt:"#FFF8DC", grayBd:"#E0E0E0", gray:"#F5F5F5", text:"#1A1A1A", textSub:"#666" };

const LS_PUBLIC_KEY = "ctrl_public_key";
const LS_MANIFEST_PREFIX = "ctrl_manifest_";
const LS_QUEUE = "ctrl_offline_queue";

/* ═══════════════════════════════════════
   Utilitaires stockage local (cache hors-ligne)
═══════════════════════════════════════ */
const loadJSON = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const saveJSON = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

/* ═══════════════════════════════════════
   Vérification de signature (Ed25519, 100% côté client)
   Reconstruit exactement l'ordre de champs utilisé par
   TicketSigningService::buildCanonicalPayload côté back.
═══════════════════════════════════════ */
const CANONICAL_ORDER = ["ref","nom","siege","depart","arrivee","date","heure","train","classe","prix","type","bookingId","tripId"];

function canonicalize(payload) {
  const ordered = {};
  for (const k of CANONICAL_ORDER) ordered[k] = payload[k] ?? "";
  return JSON.stringify(ordered);
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function verifySignature(payload, signatureB64, publicKeyB64) {
  if (!signatureB64 || !publicKeyB64) return "UNVERIFIABLE";
  try {
    const message = new TextEncoder().encode(canonicalize(payload));
    const signature = base64ToBytes(signatureB64);
    const publicKey = base64ToBytes(publicKeyB64);
    return nacl.sign.detached.verify(message, signature, publicKey) ? "VALID" : "INVALID";
  } catch {
    return "UNVERIFIABLE";
  }
}

/* ═══════════════════════════════════════
   Scanner caméra (jsQR, aucune dépendance réseau)
═══════════════════════════════════════ */
const CameraScanner = ({ onDetect, active }) => {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        tick();
      })
      .catch(() => setError("Caméra indisponible - utilisez la saisie manuelle ci-dessous."));

    const tick = () => {
      const video = videoRef.current, canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) {
          onDetect(code.data);
          return; // on laisse le parent décider de relancer le scan
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [active, onDetect]);

  if (!active) return null;

  return (
    <div style={{ position:"relative", borderRadius:14, overflow:"hidden", background:"#000", maxWidth:360, margin:"0 auto" }}>
      <video ref={videoRef} muted playsInline style={{ width:"100%", display:"block" }} />
      <canvas ref={canvasRef} style={{ display:"none" }} />
      <div style={{ position:"absolute", inset:24, border:`3px solid ${C.yellow}`, borderRadius:12, pointerEvents:"none" }} />
      {error && (
        <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.8)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", padding:20, textAlign:"center", fontSize:13 }}>
          {error}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   PAGE PRINCIPALE
═══════════════════════════════════════ */
export default function ControllerScan() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [trips, setTrips]       = useState([]);
  const [tripId, setTripId]     = useState("");
  const [manifest, setManifest] = useState(null);
  const [publicKey, setPublicKey] = useState(loadJSON(LS_PUBLIC_KEY, null));
  const [queue, setQueue]       = useState(loadJSON(LS_QUEUE, []));
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [result, setResult]     = useState(null); // { status, ticket, sigStatus, alreadyBoarded }
  const [loadingManifest, setLoadingManifest] = useState(false);
  const notify = useNotify();
  const showToast = useCallback((msg, type = "info") => notify.show(type, msg), [notify]);

  /* Connectivité */
  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  /* Clé publique - tentative de rafraîchissement si en ligne, sinon on garde le cache */
  useEffect(() => {
    if (!isOnline) return;
    api.get("/tickets/public-key").then(r => {
      if (r.data?.publicKey) {
        setPublicKey(r.data.publicKey);
        saveJSON(LS_PUBLIC_KEY, r.data.publicKey);
      }
    }).catch(() => {});
  }, [isOnline]);

  /* Liste des trains (pour choisir lequel contrôler) */
  useEffect(() => {
    if (!isOnline) return;
    api.get("/api/trips").then(r => setTrips(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [isOnline]);

  /* Synchronisation de la file d'attente hors-ligne dès que la connexion revient */
  const syncQueue = useCallback(async () => {
    const q = loadJSON(LS_QUEUE, []);
    if (!isOnline || q.length === 0) return;
    try {
      const res = await api.post("/api/admin/bookings/board-batch", { checkins: q });
      const results = res.data?.results || [];
      const failed = q.filter(item => {
        const r = results.find(x => x.bookingId === item.bookingId);
        return r && r.status === "NOT_FOUND";
      });
      saveJSON(LS_QUEUE, failed);
      setQueue(failed);
      if (q.length > failed.length) showToast(`${q.length - failed.length} contrôle(s) hors-ligne synchronisé(s).`, "success");
    } catch {
      // toujours hors-ligne ou serveur injoignable - on retentera plus tard
    }
  }, [isOnline, showToast]);

  useEffect(() => { syncQueue(); }, [isOnline, syncQueue]);

  /* Chargement (et mise en cache) du manifeste d'un train */
  const loadManifest = async (id) => {
    if (!id) return;
    setLoadingManifest(true);
    try {
      if (isOnline) {
        const r = await api.get(`/api/admin/trips/${id}/manifest`);
        setManifest(r.data);
        saveJSON(LS_MANIFEST_PREFIX + id, r.data);
      } else {
        const cached = loadJSON(LS_MANIFEST_PREFIX + id, null);
        if (cached) { setManifest(cached); showToast("Mode hors-ligne : manifeste chargé depuis le cache local.", "info"); }
        else showToast("Aucun manifeste en cache pour ce train. Connectez-vous une fois pour le télécharger.", "warning");
      }
    } catch {
      const cached = loadJSON(LS_MANIFEST_PREFIX + id, null);
      if (cached) { setManifest(cached); showToast("Serveur injoignable : manifeste chargé depuis le cache local.", "warning"); }
      else showToast("Impossible de charger le manifeste de ce train.", "error");
    } finally {
      setLoadingManifest(false);
    }
  };

  useEffect(() => { if (tripId) loadManifest(tripId); }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Traitement d'un contenu scanné ou saisi manuellement - vérifie
     directement (en ligne : recherche par référence ; hors-ligne : dans
     TOUS les manifestes déjà téléchargés), sans obliger à choisir un train. */
  const handleScanned = useCallback(async (raw) => {
    setScanning(false);
    let payload = null;
    try { payload = JSON.parse(raw); } catch { payload = null; }

    // QR scanné : payload JSON signé - vérification cryptographique directe.
    if (payload) {
      const sigStatus = verifySignature(payload, payload.sig, publicKey);
      const inManifest = manifest?.tickets?.find(t => t.bookingId === payload.bookingId || t.reference === payload.ref);
      setResult({
        status: "SCANNED", payload, sigStatus,
        ticket: inManifest || null,
        alreadyBoarded: !!(inManifest?.boardedAt) || queue.some(q => q.bookingId === (inManifest?.bookingId ?? payload.bookingId)),
      });
      return;
    }

    // Référence tapée/scannée (texte simple, ex: SETRAG-A3F7K2)
    const ref = raw.trim();

    if (isOnline) {
      try {
        const r = await api.get(`/api/admin/bookings/lookup/${encodeURIComponent(ref)}`);
        const d = r.data;
        setResult({
          status: "SCANNED",
          payload: d.payload,
          sigStatus: verifySignature(d.payload, d.signature, publicKey),
          ticket: { bookingId: d.bookingId, reference: d.reference, passenger: d.passenger, seat: d.seat, boardedAt: d.boardedAt },
          alreadyBoarded: !!d.boardedAt || queue.some(q => q.bookingId === d.bookingId),
          tripInfo: d.trip,
        });
      } catch {
        setResult({ status: "NOT_FOUND", raw });
      }
      return;
    }

    // Hors-ligne : on cherche dans TOUS les manifestes déjà téléchargés,
    // pas seulement celui du train actuellement sélectionné.
    let found = null, foundTripInfo = null;
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(LS_MANIFEST_PREFIX)) continue;
      const m = loadJSON(key, null);
      const t = m?.tickets?.find(x => x.reference === ref);
      if (t) { found = t; foundTripInfo = m.trip; break; }
    }
    if (found) {
      setResult({
        status: "FOUND_BY_REFERENCE", ticket: found, sigStatus: "UNVERIFIABLE",
        alreadyBoarded: !!found.boardedAt || queue.some(q => q.bookingId === found.bookingId),
        tripInfo: foundTripInfo,
      });
    } else {
      setResult({ status: "NOT_FOUND", raw, offlineNoManifest: true });
    }
  }, [manifest, publicKey, queue, isOnline]);

  const submitManual = () => {
    const v = manualInput.trim();
    if (!v) { showToast("Saisissez le numéro du billet (ex : SETRAG-A3F7K2).", "warning"); return; }
    handleScanned(v);
  };

  /* Validation de l'embarquement - en ligne immédiat, sinon file d'attente locale */
  const confirmBoarding = async () => {
    const bookingId = result?.ticket?.bookingId ?? result?.payload?.bookingId;
    if (!bookingId) return;

    const nowIso = new Date().toISOString();

    if (isOnline) {
      try {
        await api.post(`/api/admin/bookings/${bookingId}/board`, { boardedAt: nowIso });
        showToast(`Embarquement validé${ticketDisplay?.passenger ? ` pour ${ticketDisplay.passenger}` : ""}.`, "success");
      } catch {
        showToast("Échec de l'enregistrement : contrôle mis en file d'attente, il sera synchronisé automatiquement.", "warning");
        enqueueOffline(bookingId, nowIso);
      }
    } else {
      enqueueOffline(bookingId, nowIso);
      showToast("Hors-ligne : embarquement mis en file d'attente, il sera synchronisé automatiquement.", "info");
    }

    // Mise à jour optimiste du manifeste en cache pour éviter un double-scan
    setManifest(prev => {
      if (!prev) return prev;
      const updated = { ...prev, tickets: prev.tickets.map(t => t.bookingId === bookingId ? { ...t, boardedAt: nowIso } : t) };
      saveJSON(LS_MANIFEST_PREFIX + tripId, updated);
      return updated;
    });
    setResult(null);
    setManualInput("");
  };

  const enqueueOffline = (bookingId, boardedAt) => {
    const q = loadJSON(LS_QUEUE, []);
    q.push({ bookingId, boardedAt, boardedBy: "Contrôleur" });
    saveJSON(LS_QUEUE, q);
    setQueue(q);
  };

  const tripMeta = result?.tripInfo || manifest?.trip || null;
  const ticketDisplay = result?.ticket
    ? {
        reference: result.ticket.reference,
        passenger: result.ticket.passenger,
        seat: result.ticket.seat,
        train: tripMeta?.trainNumber || result?.payload?.train,
        route: tripMeta ? `${tripMeta.departure} → ${tripMeta.arrival}` : (result?.payload ? `${result.payload.depart} → ${result.payload.arrivee}` : ""),
        date: tripMeta?.date || result?.payload?.date,
        boardedAt: result.ticket.boardedAt,
      }
    : result?.payload
    ? {
        reference: result.payload.ref,
        passenger: result.payload.nom,
        seat: result.payload.siege,
        train: result.payload.train,
        route: `${result.payload.depart} → ${result.payload.arrivee}`,
        date: result.payload.date,
        boardedAt: null,
      }
    : null;

  return (
    <StaffFrame>
      <style>{css}</style>
      <div className="page" style={{ maxWidth: 640 }}>
        <PageHead title="Contrôle des billets" sub="Scannez ou saisissez un billet pour vérifier son authenticité et valider l'embarquement." />

        {/* Statut connexion + file d'attente */}
        <div className={`ctl-status ${isOnline ? "on" : "off"}`} role="status">
          <span className="ctl-status-main">
            {isOnline ? <><WifiIcon size={15}/> En ligne</> : <><WifiOffIcon size={15}/> Hors-ligne · vérification locale active</>}
          </span>
          {queue.length > 0 && <span className="ctl-queue">{queue.length} contrôle(s) en attente de synchronisation</span>}
        </div>

        {/* Scanner / saisie - action principale, aucune sélection de train requise */}
        {!result && (
          <div className="stf-card">
            {scanning ? (
              <>
                <CameraScanner active={scanning} onDetect={handleScanned} />
                <button className="btn btn-ghost btn-full" style={{ marginTop: 14 }} onClick={() => setScanning(false)}>Annuler le scan</button>
              </>
            ) : (
              <button className="btn btn-primary btn-full ctl-scan-btn" onClick={() => setScanning(true)}>
                <CameraIcon size={18}/> Scanner un QR code
              </button>
            )}

            <div className="ctl-or"><span>ou</span></div>

            <label className="dlabel" htmlFor="ctl-ref">Numéro du billet</label>
            <div className="ctl-manual">
              <input id="ctl-ref" className="dinput" placeholder="ex : SETRAG-A3F7K2" value={manualInput} autoCapitalize="characters"
                onChange={e => setManualInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submitManual()} />
              <button className="btn btn-primary" onClick={submitManual}>Vérifier</button>
            </div>

            {!isOnline && (
              <p className="hint hint-warn">Hors-ligne : la vérification se fait sur les manifestes déjà téléchargés (voir « Préparer le contrôle hors-ligne »).</p>
            )}
          </div>
        )}

        {/* Résultat du scan */}
        {result && (
          <div className={`stf-card ctl-result ${result.status === "NOT_FOUND" ? "bad" : result.sigStatus === "INVALID" ? "bad" : result.alreadyBoarded ? "warn" : "ok"}`}>
            {result.status === "NOT_FOUND" ? (
              <div style={{ textAlign:"center", padding:"10px 6px" }}>
                <div className="ctl-badge bad"><XIcon size={28}/></div>
                <p style={{ fontWeight:800, color:"#B91C1C", fontSize:17, margin:"10px 0 4px" }}>Billet introuvable</p>
                <p className="muted" style={{ fontSize:13, margin:0 }}>
                  {result.offlineNoManifest
                    ? "Aucun manifeste téléchargé ne contient ce billet. Reconnectez-vous ou préchargez le train concerné."
                    : "Aucun billet ne correspond à ce code. Vérifiez la saisie et réessayez."}
                </p>
                <button className="btn btn-primary btn-full" style={{ marginTop:16 }} onClick={() => setResult(null)}>Nouveau contrôle</button>
              </div>
            ) : (
              <>
                <div style={{ textAlign:"center", marginBottom:16 }}>
                  {result.sigStatus === "VALID" && <div className="ctl-sig" style={{ color:C.green }}><CheckIcon size={19}/> Signature valide</div>}
                  {result.sigStatus === "INVALID" && <div className="ctl-sig" style={{ color:"#B91C1C" }}><XIcon size={19}/> Signature invalide · billet potentiellement falsifié</div>}
                  {result.sigStatus === "UNVERIFIABLE" && <div className="ctl-sig" style={{ color:"#B45309" }}><AlertIcon size={19}/> Signature non vérifiable (clé indisponible)</div>}
                  {result.alreadyBoarded && (
                    <div className="ctl-already" role="alert">
                      <AlertIcon size={14}/> Ce billet a déjà été contrôlé {ticketDisplay?.boardedAt ? `(${new Date(ticketDisplay.boardedAt).toLocaleString("fr-FR")})` : ""}
                    </div>
                  )}
                </div>

                {ticketDisplay && (
                  <div className="ctl-ticket">
                    <div style={{ gridColumn:"1/-1" }}>
                      <div className="ctl-k">Passager</div>
                      <div style={{ fontWeight:700, fontSize:17 }}>{ticketDisplay.passenger}</div>
                    </div>
                    <div><div className="ctl-k">Référence</div><div style={{ fontWeight:600, fontFamily:"'DM Mono',monospace", overflowWrap:"anywhere" }}>{ticketDisplay.reference}</div></div>
                    <div><div className="ctl-k">Siège</div><div style={{ fontWeight:700, color:C.green }}>{ticketDisplay.seat ? `N°${ticketDisplay.seat}` : "Libre"}</div></div>
                    <div><div className="ctl-k">Trajet</div><div style={{ fontWeight:600 }}>{ticketDisplay.route}</div></div>
                    <div><div className="ctl-k">Train / Date</div><div style={{ fontWeight:600 }}>{ticketDisplay.train} · {ticketDisplay.date}</div></div>
                  </div>
                )}

                <div className="ctl-actions">
                  <button className="btn btn-ghost btn-lg" onClick={() => setResult(null)}>Scanner suivant</button>
                  {!result.alreadyBoarded && (result.ticket?.bookingId || result.payload?.bookingId) && (
                    <button className="btn btn-primary btn-lg" onClick={confirmBoarding}><CheckIcon size={16}/> Valider l'embarquement</button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Préparation hors-ligne - optionnelle, pour pouvoir contrôler sans réseau pendant le trajet */}
        <details className="stf-card ctl-offline">
          <summary>Préparer le contrôle hors-ligne (optionnel)</summary>
          <div style={{ marginTop:12 }}>
            <label className="dlabel" htmlFor="ctl-trip">Précharger un train</label>
            <select id="ctl-trip" className="dselect" value={tripId} onChange={e => setTripId(e.target.value)}>
              <option value="">- Choisir un train -</option>
              {trips.map(t => (
                <option key={t.id} value={t.id}>
                  {t.trainNumber} · {t.departure} → {t.arrival} · {t.date} {t.departureTime}
                </option>
              ))}
            </select>
            {loadingManifest && <p className="hint">Chargement du manifeste…</p>}
            {manifest && (
              <p className="hint" style={{ color:C.green }}>
                ✓ Manifeste chargé : {manifest.tickets.length} billet(s), disponible hors-ligne pour ce train.
              </p>
            )}
          </div>
        </details>

        {!publicKey && (
          <p className="hint hint-warn" style={{ marginBottom:16 }}>
            Clé de vérification non configurée côté serveur (TICKET_SIGNING_*_KEY dans .env) : les signatures ne peuvent pas être vérifiées cryptographiquement, seules les données du billet font foi.
          </p>
        )}
      </div>
    </StaffFrame>
  );
}

const css = `
.ctl-status{display:flex;align-items:center;flex-wrap:wrap;gap:6px 12px;margin-bottom:16px;padding:10px 14px;border-radius:12px;font-size:13px;}
.ctl-status.on{background:#E8F5EE;border:1.5px solid #9fd3b6;color:#004D2B;}
.ctl-status.off{background:#FEF3C7;border:1.5px solid #F3C969;color:#92400E;}
.ctl-status-main{display:flex;align-items:center;gap:7px;font-weight:700;}
.ctl-queue{margin-left:auto;font-size:12px;}
.ctl-scan-btn{padding:16px;font-size:16px;border-radius:13px;}
.ctl-or{display:flex;align-items:center;gap:10px;margin:16px 0 12px;color:#8a9e93;font-size:12px;text-transform:uppercase;letter-spacing:.1em;}
.ctl-or::before,.ctl-or::after{content:"";flex:1;height:1px;background:#ede6d6;}
.ctl-manual{display:flex;gap:8px;}
.ctl-manual input{flex:1;min-width:0;font-family:'DM Mono',monospace;letter-spacing:.04em;}
.ctl-result{border-width:2px;}
.ctl-result.ok{border-color:#9fd3b6;}
.ctl-result.warn{border-color:#F3C969;}
.ctl-result.bad{border-color:#f6b7b1;}
.ctl-badge{width:58px;height:58px;border-radius:50%;margin:0 auto;display:flex;align-items:center;justify-content:center;}
.ctl-badge.bad{background:#FEE2E2;color:#B91C1C;}
.ctl-sig{display:flex;align-items:center;justify-content:center;gap:8px;font-weight:800;font-size:16px;text-align:center;}
.ctl-already{margin-top:10px;background:#FEF3C7;color:#92400E;border-radius:10px;padding:9px 12px;font-size:13px;font-weight:600;display:flex;align-items:center;gap:7px;justify-content:center;}
.ctl-ticket{background:#f5f0e5;border-radius:12px;padding:16px;display:grid;grid-template-columns:1fr 1fr;gap:12px 16px;font-size:13.5px;margin-bottom:16px;}
.ctl-k{font-size:10px;color:#8a9e93;letter-spacing:.09em;text-transform:uppercase;font-weight:700;margin-bottom:2px;}
.ctl-actions{display:flex;gap:10px;}
.ctl-actions .btn{flex:1;justify-content:center;}
.ctl-actions .btn-primary{flex:2;}
.ctl-offline summary{cursor:pointer;font-size:13.5px;font-weight:700;color:#4b5a53;}
@media(max-width:420px){
  .ctl-ticket{grid-template-columns:1fr;}
  .ctl-actions{flex-direction:column-reverse;}
  .ctl-queue{margin-left:0;}
}
`;
